import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Divider,
  IconGrid,
  IconGridOutline,
  IconList,
  IconListOutline,
  PageContent,
  Spacer,
  Tabs,
  VisuallyHidden,
} from '@zillow/constellation';
import { Button, ButtonGroup } from '@zillow/constellation-10';

import { useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties } from '@zg-rentals/rental-manager-env';

import { AppSelectors, PropertiesSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';
import useVisibility from '../../../hooks/useVisibility';

import PropertiesActions from '../../../actions/PropertiesActions';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import accessUtils from '../../../utils/accessUtils';
import { getCurrentMonthString } from '../../../utils/dateUtils';

import { ControlledSearchBox } from '../../../components/ControlledSearchBox';

import {
  StyledFilter,
  StyledFlexContainer,
  StyledRightSubFlexContainer,
  StyledSearchBoxContainer,
  StyledStickySubHeadingSectionContainer,
  StyledTab,
  StyledTabsContainer,
  StyledTabsList,
  StyledToggleButtonGroup,
} from '../StyledComponents';

import {
  StyledTableHeader,
  StyledTableHeaderCell,
  StyledTableHeaderRow,
} from '../../../components/properties/PropertiesViewList/StyledComponents';

const { PROPERTIES_SUBNAV_FILTER_OPTIONS, PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;
const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const baseHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
];

const groupedHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET,
];

const subNavFilterOptions = {
  options: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS.reduce(
    (acc, subNav) => {
      acc[subNav.id] = { ...subNav };

      return acc;
    },
    {} as Record<string, PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE>,
  ),
};

type PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE = (typeof PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS)[0];

type Update = {
  includeActive: boolean;
  includeInactive: boolean;
  includeArchived: boolean;
  includeUnarchived: boolean;
  subNavFilterType: string;
  startKeyExclusive: string;
  includeAtRisk: boolean;
  includeFreeBonusListingsOnly: boolean;
  includeFreeClaimedListingsOnly: boolean;
};

type Props = {
  searchText: string;
  listingsLoading: boolean;
  onSearchFilterUpdate: (arg: string) => void;
  createQueryObjectAndRedirectTo: (includeArchived: boolean, searchText: string, subNavFilterType: string) => void;
  updateFiltersAndListings: ({ update }: { update: Update }) => void;
  numDisplayedListings: number;
};

const SubHeadingSection = ({
  searchText,
  listingsLoading,
  onSearchFilterUpdate,
  createQueryObjectAndRedirectTo,
  updateFiltersAndListings,
  numDisplayedListings,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  // Local state for immediate UI feedback
  const [searchInput, setSearchInput] = useState(searchText);

  // Sync searchInput with searchText when it changes from external sources
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const {
    listingCount: {
      active: numActiveListings,
      atRisk: numAtRiskListings,
      inactive: numInactiveListings,
      archived: numArchivedListings,
      freeBonusListingCount: numBonusListings,
      freeClaimedListingCount: numClaimedListings,
      total: numTotalListings,
    },
  } = useSelector((state) => PropertiesSelectors.makeProperties(state));
  const { includeArchived, viewType, subNavFilterType } = useSelector((state) =>
    PropertiesSelectors.makePropertiesFilters(state),
  );
  const user = useSelector((state) => UserSelectors.makeUser(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const [isVisible, isVisibleRef] = useVisibility(0, 100, 'above');
  const { data: { unitProperties: claimableProperties = [] } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user.features.canClaimFreeListings,
      },
    );

  const isZram = user.isEnterpriseCustomer;
  const isNonAdvertiser = accessUtils.isNonAdvertiser({ user });
  const canAccessClaimPath =
    numClaimedListings > 0 ||
    accessUtils.canAccessClaimPath({
      isScreenMobile,
      hasAnyPropertiesToClaim: claimableProperties.length > 0,
      isNonAdvertiser,
    });

  const minPropertiesForSearchDisplay = isZram ? 0 : 5;
  const canViewSearch = numTotalListings >= minPropertiesForSearchDisplay;

  useEffect(() => {
    // if screen is mobile, hide "Claimed" tab and redirect to "For rent" tab
    if (
      isScreenMobile &&
      subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED &&
      numClaimedListings === 0
    ) {
      const redirectSubNavFilterType = PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET;

      createQueryObjectAndRedirectTo(includeArchived, searchText, redirectSubNavFilterType);

      updateFiltersAndListings({
        update: {
          subNavFilterType: redirectSubNavFilterType,
          startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
          ...subNavFilterOptions.options[redirectSubNavFilterType],
        },
      });
    }
  }, [isScreenMobile]);

  const subNavFiltersOnSelectHandler = ({
    includeActive,
    includeInactive,
    id: subNavFilterType,
    includeAtRisk,
    includeFreeBonusListingsOnly,
    includeFreeClaimedListingsOnly,
    includeArchived,
    includeUnarchived,
  }: {
    includeActive: boolean;
    includeInactive: boolean;
    id: string;
    includeAtRisk: boolean;
    includeFreeBonusListingsOnly: boolean;
    includeFreeClaimedListingsOnly: boolean;
    includeArchived: boolean;
    includeUnarchived: boolean;
  }) => {
    dispatch(PropertiesActions.updateFiltersTouched(true));

    createQueryObjectAndRedirectTo(includeArchived, searchText, subNavFilterType);

    updateFiltersAndListings({
      update: {
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        subNavFilterType,
        startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
        includeAtRisk,
        includeFreeBonusListingsOnly,
        includeFreeClaimedListingsOnly,
      },
    });

    PropertiesPageCSEvents.PropertyListFilter({
      user,
      triggerObject: PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS[subNavFilterType],
    });
  };

  const countMap = {
    [PROPERTIES_SUBNAV_FILTER_TYPE.ALL]: numTotalListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET]: numActiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET]: numInactiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK]: numAtRiskListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED]: numClaimedListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS]: numBonusListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED]: numArchivedListings,
  };
  PROPERTIES_SUBNAV_FILTER_OPTIONS.forEach((subNav) => {
    subNavFilterOptions.options[subNav.id].count = countMap[subNav.id] || 0;
  });

  const isListView = viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST;
  const isGridView = viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID;
  const placeholderText =
    user.isEnterpriseCustomer && !isScreenLgGTE ? 'City, address or ZIP' : 'Search by city, address and ZIP code';

  const handleViewSwitcherClick = (
    viewType: (typeof PropertiesConstants.PROPERTIES_VIEW_TYPE)[keyof typeof PropertiesConstants.PROPERTIES_VIEW_TYPE],
  ) => {
    dispatch(PropertiesActions.updateFiltersTouched(true));
    dispatch(PropertiesActions.updateFilters({ viewType }));
  };

  const handleSubNavFilterChipClick = (id: string) => {
    // treat archived filter as a toggle between all and archived view when there are no unarchived listings
    if (id === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && numTotalListings === 0) {
      subNavFiltersOnSelectHandler(
        subNavFilterOptions.options[PROPERTIES_SUBNAV_FILTER_TYPE.ALL] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE,
      );
    }
  };

  const renderSubNavFilterChip = (id: string, displayLabel: string) => {
    return (
      <StyledFilter
        key={id}
        id={id}
        value={id}
        isActive={id === subNavFilterType}
        disabled={listingsLoading}
        onClick={() => handleSubNavFilterChipClick(id)}
        data-eventid={PropertiesPageCSEvents.PropertyListFilter.data_event_id}
      >
        {displayLabel}
      </StyledFilter>
    );
  };

  const renderSubNavButton = ({ id, label, count }: PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE) => {
    const displayLabel = `${label} (${count})`;

    const isBaseHideableTab = baseHideableTabs.includes(id) && count === 0 && id !== subNavFilterType;
    const isGroupedHideableTab = !isZram && groupedHideableTabs.includes(id) && count === 0 && numTotalListings === 0;

    const isClaimedTab = id === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED;

    if (isBaseHideableTab || isGroupedHideableTab || (isClaimedTab && !canAccessClaimPath)) {
      return <Fragment key={id} />;
    }

    if (!isZram) {
      return isScreenLgGTE && id === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED
        ? [
            numTotalListings > 0 && <Divider orientation="vertical" marginX="xs" />,
            renderSubNavFilterChip(id, displayLabel),
          ]
        : renderSubNavFilterChip(id, displayLabel);
    }

    return id === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED ? (
      <Fragment key={id} />
    ) : (
      <StyledTab
        key={id}
        id={id}
        isActive={id === subNavFilterType}
        disabled={listingsLoading}
        wrapAtXL={canAccessClaimPath}
        data-eventid={PropertiesPageCSEvents.PropertyListFilter.data_event_id}
      >
        {displayLabel}
      </StyledTab>
    );
  };

  const renderSearchAndFilters = () => (
    <PageContent
      data-testid="SubHeadingSection"
      marginBottom={!isGridView && (!isScreenLgGTE || !isZram) ? '0' : 'md'}
      {...(!isZram && {
        padding: 'xs',
        marginTop: { default: 'md', lg: '0' },
      })}
    >
      {
        <StyledFlexContainer
          data-testid="SubHeadingSection-Row"
          display="flex"
          alignItems="center"
          paddingX={isZram ? 'sm' : { default: '0', lg: 'sm' }}
          paddingY={isZram ? 'sm' : { default: '0', lg: 'sm' }}
          wrapAtXL={canAccessClaimPath}
          isZram={isZram}
        >
          {canViewSearch && (
            <StyledRightSubFlexContainer wrapAtXL={canAccessClaimPath}>
              <StyledSearchBoxContainer>
                <ControlledSearchBox
                  placeholder={placeholderText}
                  value={searchInput}
                  onChange={setSearchInput}
                  onSearch={onSearchFilterUpdate}
                  isSearching={listingsLoading && Boolean(searchText)}
                  disabled={!isListView && !isGridView}
                />
              </StyledSearchBoxContainer>

              {isZram && (
                <Spacer marginLeft="sm">
                  <ButtonGroup aria-label="Properties switcher button-group" conjoined={true}>
                    <Button
                      onClick={() => handleViewSwitcherClick(PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST)}
                      tone={isListView && !isGridView ? 'brand' : 'neutral'}
                      emphasis={isListView && !isGridView ? 'filled' : 'outlined'}
                      disabled={!isListView && !isGridView}
                    >
                      {isListView && !isGridView ? (
                        <IconList className="PropertiesPage-ListingViewSwitcher" />
                      ) : (
                        <IconListOutline className="PropertiesPage-ListingViewSwitcher" />
                      )}
                    </Button>
                    <Button
                      onClick={() => handleViewSwitcherClick(PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID)}
                      tone={isGridView && !isListView ? 'brand' : 'neutral'}
                      emphasis={isGridView && !isListView ? 'filled' : 'outlined'}
                      disabled={!isListView && !isGridView}
                    >
                      {isGridView && !isListView ? (
                        <IconGrid className="PropertiesPage-ListingViewSwitcher" />
                      ) : (
                        <IconGridOutline className="PropertiesPage-ListingViewSwitcher" />
                      )}
                    </Button>
                  </ButtonGroup>
                </Spacer>
              )}
            </StyledRightSubFlexContainer>
          )}
          {(!isZram || !includeArchived) && (
            <StyledTabsContainer
              data-testid="SubNav Tab Container"
              wrapAtXL={canAccessClaimPath}
              isZram={isZram}
              canViewSearch={canViewSearch}
            >
              {!isZram ? (
                <StyledToggleButtonGroup
                  aria-label="SubNav Filter Tabs"
                  isScreenLgGTE={isScreenLgGTE}
                  value={subNavFilterType}
                  onChange={(id: string) =>
                    subNavFiltersOnSelectHandler(
                      subNavFilterOptions.options[id] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE,
                    )
                  }
                >
                  {Object.keys(subNavFilterOptions.options).map((id) =>
                    renderSubNavButton(subNavFilterOptions.options[id] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE),
                  )}
                </StyledToggleButtonGroup>
              ) : (
                <Tabs selected={subNavFilterType}>
                  <StyledTabsList
                    aria-label="SubNav Filter Tabs"
                    onSelected={(id: string) =>
                      subNavFiltersOnSelectHandler(
                        subNavFilterOptions.options[id] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE,
                      )
                    }
                  >
                    {Object.keys(subNavFilterOptions.options).map((id) =>
                      renderSubNavButton(subNavFilterOptions.options[id] as PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE),
                    )}
                  </StyledTabsList>
                </Tabs>
              )}
            </StyledTabsContainer>
          )}
        </StyledFlexContainer>
      }
    </PageContent>
  );

  const isStaticPosition = !!isVisible || numDisplayedListings <= 5;

  return (
    <>
      {isZram && renderSearchAndFilters()}

      {!isZram && (
        <>
          <div ref={isVisibleRef as React.RefObject<HTMLDivElement>} />
          <StyledStickySubHeadingSectionContainer isVisible={isStaticPosition || listingsLoading}>
            {renderSearchAndFilters()}
            {!listingsLoading && isScreenLgGTE && numDisplayedListings > 0 && (
              <StyledTableHeader appearance="dark" isVisible={isStaticPosition}>
                <StyledTableHeaderRow>
                  <StyledTableHeaderCell isZram={isZram} scope="col" id={PROPERTY_TABLE_COLUMNS.ADDRESS}>
                    Address
                  </StyledTableHeaderCell>
                  {subNavFilterType !== PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED && (
                    <>
                      <StyledTableHeaderCell isZram={isZram} scope="col" id={PROPERTY_TABLE_COLUMNS.RENT}>
                        {getCurrentMonthString(new Date())} rent
                      </StyledTableHeaderCell>
                      <StyledTableHeaderCell isZram={isZram} scope="col" id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>
                        Potential tenants
                      </StyledTableHeaderCell>
                      <StyledTableHeaderCell isZram={isZram} scope="col" id={PROPERTY_TABLE_COLUMNS.LEASES}>
                        Lease
                      </StyledTableHeaderCell>
                      <StyledTableHeaderCell isZram={isZram} scope="col" id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                        <VisuallyHidden>Actions</VisuallyHidden>
                      </StyledTableHeaderCell>
                    </>
                  )}
                </StyledTableHeaderRow>
              </StyledTableHeader>
            )}
          </StyledStickySubHeadingSectionContainer>
        </>
      )}
    </>
  );
};

export default SubHeadingSection;
